import { shallowRef } from 'vue'
import { cloneDeep, isEqual } from 'lodash-es'
import type { RouteLocationNormalized } from 'vue-router'
import { useModalStore } from '@/stores/modalStore'

// Module-level registry of every mounted surface that can hold unsaved,
// in-memory edits — an editor, a side pane, a full-screen overlay, a create
// modal. Deliberately shaped like `useDismissablePriority` in lab-components
// (same LIFO stack, same activate-on-mount / deactivate-on-unmount lifecycle),
// but it answers a different question: that one decides who reacts to Escape,
// this one decides whether leaving would throw work away.
//
// The two questions need different answers. Escape is always about the
// top-most layer; unsaved changes are about *any* layer, because a clean
// modal opened on top of a dirty editor must not hide the editor's edits from
// the guard (browser back still works while a modal covers the screen, and
// some modals navigate on their own via $router.push).
interface IUnsavedGuardClaim {
  id: number
  isDirty: () => boolean
  // Navigations that stay inside this surface — e.g. the pricing segment
  // editor's own Generelt / Analyser / Svartider tabs, which are child routes
  // over one shared draft. Evaluated per claim, not once against the top of
  // the stack, so one editor's tab switch never suppresses another layer's
  // warning.
  isInternalNavigation?: (to: RouteLocationNormalized) => boolean
  // Set by surfaces living in the modal stack, to the `ModalEntry` id they
  // were opened under. Lets ModalHost tell "the modal I am about to close is
  // dirty" apart from "something underneath it is dirty" — closing a modal
  // does not unmount what is beneath it, so only the former should warn.
  modalId?: string
}

const claims: IUnsavedGuardClaim[] = []
let nextId = 0

// Set while a leave has been confirmed, so the navigation the guard just let
// through is not re-checked by a second guard invocation (Vue Router re-runs
// beforeEach for the redirect a cancelled navigation can produce).
let isLeaveConfirmed = false

// What a surface passes to `register`. Give it whichever you already have:
//
// - `snapshot` — the default for a surface with no dirty tracking of its own.
//   The guard keeps the baseline and does the comparison, so the component
//   needs no lastSavedSnapshot field and no isDirty computed at all. Call
//   `takeSnapshot()` again after each successful save.
// - `isDirty` — for a surface that already computes dirtiness for its own
//   reasons (e.g. to disable its save button). Nothing to duplicate.
type TRegisterOptions = Omit<IUnsavedGuardClaim, 'id' | 'isDirty'> &
  (
    | { snapshot: () => unknown; isDirty?: never }
    | { isDirty: () => boolean; snapshot?: never }
  )

export function useUnsavedChangesGuard() {
  const id = nextId++

  // shallowRef, not a plain closure variable: a component wrapping `isDirty()`
  // in a computed (to disable its save button) must re-evaluate when the
  // baseline moves after a save, and only a ref makes that reactive.
  const baseline = shallowRef<unknown>(undefined)
  let getSnapshot: (() => unknown) | null = null

  function takeSnapshot() {
    if (!getSnapshot) return
    baseline.value = cloneDeep(getSnapshot())
  }

  function isDirty(): boolean {
    if (!getSnapshot || baseline.value === undefined) return false
    return !isEqual(getSnapshot(), baseline.value)
  }

  function register(options: TRegisterOptions) {
    if (claims.some((claim) => claim.id === id)) {
      unregister()
    }

    const { snapshot, isDirty: ownIsDirty, ...rest } = options
    if (snapshot) {
      getSnapshot = snapshot
      takeSnapshot()
    }

    claims.push({ id, ...rest, isDirty: ownIsDirty ?? isDirty })
  }

  function unregister() {
    const index = claims.findIndex((claim) => claim.id === id)
    if (index !== -1) {
      claims.splice(index, 1)
    }
  }

  return { register, unregister, takeSnapshot, isDirty }
}

// A modal that has been popped off the stack is on its way out, but Vue does
// not unmount it until the next tick — so its claim is still registered, and
// still dirty, for the rest of the current one. That window is not theoretical:
// several create modals call closeTop() and then $router.push() to the record
// they just made, synchronously. Without this filter the user would be warned
// about losing work they had in fact just saved.
function isLive(claim: IUnsavedGuardClaim): boolean {
  if (!claim.modalId) return true
  return useModalStore().stack.some((entry) => entry.id === claim.modalId)
}

/** True when any registered surface currently holds unsaved edits. */
export function hasUnsavedChanges(): boolean {
  return claims.some((claim) => isLive(claim) && claim.isDirty())
}

/**
 * The claims a navigation to `to` would actually throw away: every dirty one
 * that does not consider `to` internal to itself.
 */
export function getBlockingClaims(
  to: RouteLocationNormalized
): IUnsavedGuardClaim[] {
  return claims.filter(
    (claim) =>
      isLive(claim) && claim.isDirty() && !claim.isInternalNavigation?.(to)
  )
}

/** True when the modal opened under `modalId` holds unsaved edits. */
export function isModalDirty(modalId: string): boolean {
  return claims.some((claim) => claim.modalId === modalId && claim.isDirty())
}

/**
 * Opens the confirmation and resolves true when the user chose to leave.
 *
 * Uses the normal modal stack, so this sits on top of whatever is already
 * open — including the very modal being closed, which is why the ModalHost
 * close path has to pop twice once this resolves true.
 *
 * Escape dismisses it (the store's default), which resolves false — the same
 * answer as the cancel button and as ModalHost's X, which could already
 * dismiss it. Blocking Escape here bought nothing: the dialog was never
 * un-dismissable, only un-dismissable *by keyboard*, and this dialog is most
 * often reached by pressing Escape on a dirty modal in the first place.
 * Escape can never pick the destructive answer, since dismissing means stay.
 */
export function confirmLeaveUnsaved(): Promise<boolean> {
  const modalStore = useModalStore()

  return new Promise<boolean>((resolve) => {
    modalStore.open({
      name: 'UnsavedChangesModal',
      hideCloseButton: true,
      props: {
        onLeave: () => resolve(true),
        onStay: () => resolve(false),
      },
    })
  })
}

// Exported for the router guard only — see the comment on `isLeaveConfirmed`.
export function consumeLeaveConfirmation(): boolean {
  const wasConfirmed = isLeaveConfirmed
  isLeaveConfirmed = false
  return wasConfirmed
}

export function markLeaveConfirmed() {
  isLeaveConfirmed = true
}

// Test-only: the registry is module state, so it survives between specs.
export function resetUnsavedChangesGuard() {
  claims.length = 0
  isLeaveConfirmed = false
}
